import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layouts/user';
import UserHead from '../../components/user/head';
import { Errors } from '../../components/common/msg';
import { get } from '../../libs/api';
import { isOkResponse, responseData } from '../../libs/apiResponse';
import {
  bondStatus,
  priceSourceName,
  showBondDate,
  showParPrice,
  showRate,
  todayIso,
  yearsToMaturity,
} from '../../libs/bond';
import './bond.css';

// Bond Master (PB-2339): every bond, held or not, searchable and
// grouped by time to maturity, each linking to its detail page.

const FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'PERPETUAL', label: 'Perpetual' },
  { key: 'MATURED', label: 'Matured' },
];

const SORTS = [
  { key: 'maturity', label: 'Maturity' },
  { key: 'name', label: 'Name' },
  { key: 'coupon', label: 'Coupon' },
];

const BUCKETS = [
  'Maturing within 3 years',
  'Maturing in 3–10 years',
  'Maturing after 10 years',
  'Perpetual',
  'Matured',
];

const bucketOf = (bond, today) => {
  const status = bondStatus(bond, today);
  if (status === 'MATURED') return 'Matured';
  if (status === 'PERPETUAL') return 'Perpetual';
  const years = yearsToMaturity(bond, today);
  if (years <= 3) return 'Maturing within 3 years';
  return years <= 10 ? 'Maturing in 3–10 years' : 'Maturing after 10 years';
};

const couponText = (bond) => {
  if (bond.couponFrequency === 0 || bond.couponRate === 0) return 'Zero';
  return bond.couponRate == null ? '-' : showRate(bond.couponRate);
};

const compare = {
  name: (a, b) => a.name.localeCompare(b.name),
  coupon: (a, b) => (Number(b.couponRate) || 0) - (Number(a.couponRate) || 0),
  maturity: (a, b) => (a.maturityDate || '9999').localeCompare(b.maturityDate || '9999'),
};

const BondRow = ({ bond, first }) => {
  const price = bond.indicativePrice;
  return (
    <Link
      to={'/portal/bonds/' + bond.securitymasterId}
      className={`d-flex align-items-center px-4 py-3 bond-row bond-card-link ${
        first ? '' : 'border-top'
      }`}
    >
      <div className="flex-grow-1 text-truncate">
        <div className="lead-text text-truncate">{bond.name}</div>
        <div className="sub-text text-truncate">
          {bond.issuer ? `${bond.issuer} · ` : ''}
          <span className="bond-isin">{bond.isin}</span>
        </div>
      </div>
      <div className="text-right d-none d-sm-block bond-col-coupon">
        <div className="lead-text">{couponText(bond)}</div>
        <div className="sub-text">Coupon</div>
      </div>
      <div className="text-right d-none d-md-block bond-col-maturity">
        <div className="lead-text">
          {bond.maturityDate ? showBondDate(bond.maturityDate) : 'Perpetual'}
        </div>
        <div className="sub-text">Maturity</div>
      </div>
      <div className="text-right bond-col-price">
        <div className="lead-text">{price ? showParPrice(price.price) : '-'}</div>
        <div className="sub-text text-truncate">{price ? priceSourceName(price) : 'No price'}</div>
      </div>
      <em className="icon ni ni-forward-ios text-primary"></em>
    </Link>
  );
};

const Component = () => {
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [sort, setSort] = useState('maturity');
  const today = todayIso();

  useEffect(() => {
    let current = true;
    const load = async () => {
      setLoading(true);
      setErrors([]);
      try {
        const resp = await get('/api/bond/list');
        if (!current) return;
        if (!isOkResponse(resp)) {
          setErrors(['Bonds could not be loaded. Please try again later.']);
          return;
        }
        setBonds(responseData(resp, []) || []);
      } catch (e) {
        if (current) setErrors(['Bonds could not be loaded. Please try again later.']);
      } finally {
        if (current) setLoading(false);
      }
    };
    load();
    return () => {
      current = false;
    };
  }, []);

  const term = query.trim().toLowerCase();
  const count = (key) => bonds.filter((b) => key === 'ALL' || bondStatus(b, today) === key).length;
  const visible = bonds
    .filter((b) => filter === 'ALL' || bondStatus(b, today) === filter)
    .filter(
      (b) => !term || [b.name, b.isin, b.issuer].some((s) => (s || '').toLowerCase().includes(term))
    )
    .sort(compare[sort]);

  return (
    <Layout menu="bonds">
      <UserHead
        title="Bond Master"
        description="Master data for every bond, including ones you do not hold."
      />

      <Errors errors={errors} />

      {!loading && errors.length === 0 && (
        <div className="bond-page">
          <div className="card card-bordered bond-toolbar mb-3">
            <div className="card-inner py-3">
              <div className="d-flex flex-wrap align-items-center bond-toolbar-row">
                <div className="form-control-wrap bond-search flex-grow-1">
                  <div className="form-icon form-icon-left">
                    <em className="icon ni ni-search"></em>
                  </div>
                  <input
                    className="form-control"
                    placeholder="Search by name, ISIN or issuer"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <ul className="nk-block-tools gx-2 mb-0 bond-filters">
                  {FILTERS.map((f) => (
                    <li key={f.key}>
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          filter === f.key ? 'btn-primary' : 'btn-light btn-white'
                        }`}
                        onClick={() => setFilter(f.key)}
                      >
                        {f.label} <span className="ml-1">{count(f.key)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                <select
                  className="form-select form-control w-auto bond-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  aria-label="Sort bonds"
                >
                  {SORTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      Sort: {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {visible.length === 0 && (
            <div className="card card-bordered">
              <div className="card-inner text-center sub-text">
                {bonds.length === 0 ? 'No records to show' : 'No bonds match your search.'}
              </div>
            </div>
          )}

          {BUCKETS.map((bucket) => {
            const rows = visible.filter((b) => bucketOf(b, today) === bucket);
            if (rows.length === 0) return null;
            return (
              <div className="mb-4" key={bucket}>
                <div className="overline-title-alt mb-2">
                  {bucket} · {rows.length}
                </div>
                <div className="card card-bordered">
                  {rows.map((b, i) => (
                    <BondRow key={b.securitymasterId} bond={b} first={i === 0} />
                  ))}
                </div>
              </div>
            );
          })}

          <p className="sub-text mt-2">Prices are shown as a percentage of par.</p>
        </div>
      )}
    </Layout>
  );
};

export default Component;
